import React from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  z-index: 10000;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(5px);
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  background: rgba(30, 30, 30, 0.95);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  animation: slideIn 0.3s ease;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: scale(0.9) translateY(-20px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px 20px 0 0;
`;

const ModalTitle = styled.h2`
  color: #ffd700;
  margin: 0;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:before {
    content: '📊';
  }
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(248, 113, 113, 0.3);
    transform: rotate(90deg);
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
  overflow: auto;
  max-width: calc(90vw - 3rem);
  max-height: calc(90vh - 100px);
`;

const DataFrameTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  overflow: hidden;
  font-family: 'Fira Code', 'Courier New', monospace;
  font-size: 0.85rem;
`;

const TableHeader = styled.thead`
  background: rgba(102, 126, 234, 0.2);
  position: sticky;
  top: 0;
  z-index: 10;
`;

const TableHeaderRow = styled.tr`
  border-bottom: 2px solid rgba(255, 255, 255, 0.2);
`;

const TableHeaderCell = styled.th`
  padding: 0.75rem 1rem;
  text-align: left;
  font-weight: bold;
  color: #ffd700;
  white-space: nowrap;
  user-select: none;
`;

const RowNumberHeader = styled.th`
  padding: 0.75rem 1rem;
  text-align: center;
  font-weight: bold;
  color: #ffd700;
  background: rgba(102, 126, 234, 0.3);
  border-right: 2px solid rgba(255, 255, 255, 0.2);
  white-space: nowrap;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr<{ $index: number }>`
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  &:hover {
    background: rgba(102, 126, 234, 0.1);
  }

  ${props => props.$index % 2 === 0 && `
    background: rgba(255, 255, 255, 0.02);

    &:hover {
      background: rgba(102, 126, 234, 0.1);
    }
  `}
`;

const RowNumberCell = styled.td`
  padding: 0.75rem 1rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 500;
  background: rgba(0, 0, 0, 0.2);
  border-right: 2px solid rgba(255, 255, 255, 0.2);
  white-space: nowrap;
`;

const TableCell = styled.td<{ $isNumeric?: boolean }>`
  padding: 0.75rem 1rem;
  color: ${props => props.$isNumeric ? '#4ade80' : 'rgba(255, 255, 255, 0.9)'};
  white-space: nowrap;
  font-family: ${props => props.$isNumeric ? "'Fira Code', monospace" : "inherit"};
`;

const DataFrameInfo = styled.div`
  margin-bottom: 1rem;
  padding: 1rem;
  background: rgba(102, 126, 234, 0.1);
  border-radius: 8px;
  border-left: 4px solid #667eea;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
`;

const EmptyMessage = styled.div`
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
  padding: 2rem;
  font-size: 1rem;
`;

const Navigation = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background: rgba(0, 0, 0, 0.3);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0 0 20px 20px;
  flex-shrink: 0;
`;

const NavButton = styled.button<{ direction: 'prev' | 'next' }>`
  padding: 0.6rem 1.2rem;
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  border: none;
  border-radius: 12px;
  color: white;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 3px 10px rgba(240, 147, 251, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  ${props => props.direction === 'prev' && `
    &:before {
      content: '←';
    }
  `}

  ${props => props.direction === 'next' && `
    &:after {
      content: '→';
    }
  `}
`;

const DataFrameCounter = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 1rem;
  font-weight: 500;
`;

export interface DataFrameColumn {
  name: string;
  type: string;
  values: (string | number | boolean | null)[];
}

export interface DataFrameData {
  name: string;
  columns: DataFrameColumn[];
  rowCount: number;
  colCount: number;
}

interface DataFrameModalProps {
  isOpen: boolean;
  dataFrames: DataFrameData[];
  currentIndex: number;
  onClose: () => void;
  onNext: () => void;
  onPrevious: () => void;
}

export const DataFrameModal: React.FC<DataFrameModalProps> = ({
  isOpen,
  dataFrames,
  currentIndex,
  onClose,
  onNext,
  onPrevious
}) => {
  const dataFrame = dataFrames[currentIndex] || null;
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          onPrevious();
          break;
        case 'ArrowRight':
          onNext();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, onPrevious, onNext]);

  if (!isOpen || !dataFrame) {
    return null;
  }

  const isNumericColumn = (type: string) => {
    return ['double', 'integer', 'numeric'].includes(type);
  };

  return createPortal(
    <ModalOverlay isOpen={isOpen} onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Data Frame: {dataFrame.name}</ModalTitle>
          <CloseButton onClick={onClose} title="关闭 (ESC)">
            ×
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <DataFrameInfo>
            Dimensions: {dataFrame.rowCount} rows × {dataFrame.colCount} columns
          </DataFrameInfo>

          {dataFrame.columns.length === 0 ? (
            <EmptyMessage>Empty data frame</EmptyMessage>
          ) : (
            <DataFrameTable>
              <TableHeader>
                <TableHeaderRow>
                  <RowNumberHeader>#</RowNumberHeader>
                  {dataFrame.columns.map(col => (
                    <TableHeaderCell key={col.name}>
                      {col.name}
                      <span style={{ fontSize: '0.75rem', opacity: 0.7, marginLeft: '0.5rem' }}>
                        ({col.type})
                      </span>
                    </TableHeaderCell>
                  ))}
                </TableHeaderRow>
              </TableHeader>
              <TableBody>
                {dataFrame.columns[0]?.values.map((_, rowIndex) => (
                  <TableRow key={rowIndex} $index={rowIndex}>
                    <RowNumberCell>{rowIndex + 1}</RowNumberCell>
                    {dataFrame.columns.map(col => (
                      <TableCell
                        key={col.name}
                        $isNumeric={isNumericColumn(col.type)}
                      >
                        {col.values[rowIndex] === null ? (
                          <span style={{ opacity: 0.5 }}>NA</span>
                        ) : col.values[rowIndex] === null ? (
                          <span style={{ opacity: 0.5 }}>NULL</span>
                        ) : (
                          String(col.values[rowIndex])
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </DataFrameTable>
          )}
        </ModalBody>

        {dataFrames.length > 1 && (
          <Navigation>
            <NavButton
              direction="prev"
              onClick={onPrevious}
              disabled={currentIndex === 0}
            >
              上一个
            </NavButton>
            <DataFrameCounter>
              {currentIndex + 1} / {dataFrames.length}
            </DataFrameCounter>
            <NavButton
              direction="next"
              onClick={onNext}
              disabled={currentIndex === dataFrames.length - 1}
            >
              下一个
            </NavButton>
          </Navigation>
        )}
      </ModalContent>
    </ModalOverlay>,
    document.body
  );
};
